import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AuthCallback - Handles Google OAuth callback
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * This component:
 * 1. Extracts session_id from URL fragment
 * 2. Exchanges it with backend for user session
 * 3. Redirects to appropriate dashboard
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment (hash)
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigate('/login', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Clear the hash from URL immediately (for security)
        window.history.replaceState(null, '', window.location.pathname);

        // Exchange session_id with backend
        const response = await fetch(`${API_URL}/api/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Authentication failed');
        }

        const data = await response.json();
        
        if (data.success && data.user) {
          // Store user info temporarily for the dashboard
          sessionStorage.setItem('google_user', JSON.stringify(data.user));
          
          // Redirect based on user role
          const redirectPath = data.user.role === 'admin' ? '/admin' : '/';
          navigate(redirectPath, { 
            replace: true,
            state: { user: data.user, fromGoogleAuth: true }
          });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { 
          replace: true, 
          state: { error: 'Google sign-in failed. Please try again.' } 
        });
      }
    };

    processAuth();
  }, [navigate, location.hash]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
        <p className="mt-4 text-gray-600 font-medium">Completing sign in...</p>
        <p className="mt-2 text-sm text-gray-500">Please wait while we verify your account</p>
      </div>
    </div>
  );
}
